import { Vector2 } from 'three';
import { assertDefined } from '../utils/debug';
import { registerType, Serializable } from '../utils/serialization.util';
import {
  addLevel,
  alignMapOffset,
  chunkIdForOffset,
  chunkOffsetForId,
  computeTileMapLocation,
  chunksCoveredByTile,
  getImageTileByChunk,
  imageChunkPlanogramPosition,
  levelRatio,
  LodLevelMask,
  mapOffsetToIndex,
  removeLevel,
} from './helpers';
import { ImageData, ImageId, TileLodData } from './interfaces';
import PhysicalTextures, { SlotIndex } from './PhysicalTextures';
import { TextureCache } from './TextureCache';
import { TileMap, TRANSPARENT_TILE } from './TileMap';
import ChunkSpatialTree from './ChunkSpatialTree';
import PlanogramPoint from '../utils/PlanogramPoint';
import CanceledError from '../errors/Canceled';

export class SlotMap {
  private slotMap: Array<Map<number, SlotIndex>> = []; // level => (chunkId => slotId)

  get(level: number, chunkId: number): SlotIndex | undefined {
    return this.slotMap[level]?.get(chunkId);
  }

  set(level: number, chunkId: number, slot: SlotIndex): void {
    this.slotMap[level] = this.slotMap[level] ?? new Map();
    this.slotMap[level].set(chunkId, slot);
  }

  isEmpty(level: number, chunkId: number): boolean {
    return this.slotMap[level]?.get(chunkId) === undefined;
  }
  pop(level: number, chunkId: number): SlotIndex | undefined {
    const slot = this.get(level, chunkId);
    if (slot !== undefined) this.slotMap[level].delete(chunkId);
    return slot;
  }
}

function addLevelToState(
  context: SelectionContext,
  image: ImageData,
  level: number,
  chunkId: number,
) {
  const state = context.currentState;
  const mapOffset = alignMapOffset(
    image,
    level,
    chunkOffsetForId(chunkId, context.tileMapResolution),
  );
  const tileSize = levelRatio(image.lodData, level);

  const offset = new Vector2();
  for (let x = 0; x < tileSize; x++) {
    for (let y = 0; y < tileSize; y++) {
      offset.set(x, y).add(mapOffset);
      const id = chunkIdForOffset(offset, context.tileMapResolution);
      state[id] = addLevel(state[id], level);

      context.updateChunkTreeData(image, id, imageChunkPlanogramPosition(image, offset));
    }
  }
}

function removeLevelFromState(
  context: SelectionContext,
  image: ImageData,
  level: number,
  chunkId: number,
) {
  const state = context.currentState;
  const tileSize = levelRatio(image.lodData, level);
  const mapOffset = alignMapOffset(
    image,
    level,
    chunkOffsetForId(chunkId, context.tileMapResolution),
  );

  const offset = new Vector2();
  for (let x = 0; x < tileSize; x++) {
    for (let y = 0; y < tileSize; y++) {
      offset.set(x, y).add(mapOffset);
      const id = chunkIdForOffset(offset, context.tileMapResolution);
      state[id] = removeLevel(state[id], level);

      context.updateChunkTreeData(image, id, imageChunkPlanogramPosition(image, offset));
    }
  }
}

export function cleanSlot(
  context: LoaderContext,
  image: ImageData,
  level: number,
  chunkId: number,
) {
  const mapOffset = context.tileMap.chunkOffset(chunkId);
  const index = mapOffsetToIndex(image, mapOffset, level);
  const lodLevel = image.lodData.curator_lods[level];
  const tile = lodLevel.textures[index];
  assertDefined(tile, 'Invalid tile index');

  const oldSlot = context.slotMap.pop(level, chunkId);
  if (oldSlot !== undefined) context.physicalTextures.freeSlot(oldSlot);
}

export interface LoaderContext {
  images: Map<ImageId, ImageData>;
  textureCache: TextureCache;
  physicalTextures: PhysicalTextures;
  cdnUrl: string;
  tileMap: TileMap;
  slotMap: SlotMap;
}

export interface SelectionContext {
  unloadedLevelBias: number;
  images: Map<ImageId, ImageData>;
  currentState: LodLevelMask[];
  tileMapResolution: number;
  chunkTree: ChunkSpatialTree;
  updateChunkTreeData(image: ImageData, chunkId: number, chunkPosition: PlanogramPoint): void;
}

export interface Operation {
  name: string;
  reservedSlots: number;
  usedSlots: number; // only guaranteed to hold the correct value after preloading
  affectedChunkIds: Array<number>; // use a more efficient format, all operations affect a square of power of 2 size
  causeChunkIds: number[];
  initialize: (context: SelectionContext) => void;
  applyState: (context: SelectionContext) => void;
  revertState: (context: SelectionContext) => void;
  preload: (context: LoaderContext) => Promise<void>;
  cancel: (context: LoaderContext) => void; // Must prevent side-effects if called before execute()
  execute: (context: LoaderContext) => void;
  toString: () => string;
}

export class LoadOperation implements Operation, Serializable {
  constructor(
    imageId: string,
    public level: number,
    public chunkId: number,
    public causeChunkIds: number[],
  ) {
    this.name = 'load';
    this.imageId = imageId;
  }
  __className(): string {
    return 'LoadOperation';
  }

  imageId: string;
  name: string;
  reservedSlots: number;
  usedSlots: number;
  affectedChunkIds: number[];
  private tile: TileLodData | null;
  private canceled: boolean = false;
  private tileUrl: string;
  private slot: number | undefined;

  applyState(context: SelectionContext) {
    addLevelToState(context, context.images.get(this.imageId)!, this.level, this.chunkId);
  }

  revertState(context: SelectionContext) {
    removeLevelFromState(context, context.images.get(this.imageId)!, this.level, this.chunkId);
  }

  initialize(context: SelectionContext) {
    const image = context.images.get(this.imageId);
    assertDefined(image, 'Image was removed during operation');
    const isEmpty =
      getImageTileByChunk(image, this.level, this.chunkId, context.tileMapResolution) === null;
    this.reservedSlots = isEmpty ? 0 : 1;
    this.affectedChunkIds = chunksCoveredByTile(
      image.lodData,
      this.chunkId,
      this.level,
      context.tileMapResolution,
    );
  }

  preload(context: LoaderContext): Promise<void> {
    const image = context.images.get(this.imageId);
    assertDefined(image, 'Image was removed during operation');
    const lodLevel = image.lodData.curator_lods[this.level];
    this.tile = getImageTileByChunk(image, this.level, this.chunkId, context.tileMap.resolution);
    assertDefined(this.tile, 'Invalid tile index');

    if (this.tile === null) {
      this.usedSlots = 0;
      return Promise.resolve();
    }

    this.tileUrl = `${context.cdnUrl}/${lodLevel.url_start}/${this.tile.url}.webp`;
    return context.textureCache.load(this.tileUrl).then(texture => {
      if (this.canceled) throw new CanceledError();
      const storage = context.physicalTextures.storeTile(texture);
      this.slot = storage.slot;
      this.usedSlots = storage.cost;
      return storage.loaded;
    });
  }

  cancel(context: LoaderContext) {
    const image = context.images.get(this.imageId);
    assertDefined(image, 'Image was removed during operation');
    this.canceled = true;
    context.textureCache.cancel(this.tileUrl);
    if (this.slot !== undefined) {
      context.physicalTextures.freeSlot(this.slot);
    }
  }

  execute(context: LoaderContext) {
    const image = context.images.get(this.imageId);
    assertDefined(image, 'Image was removed during operation');
    const mapOffset = chunkOffsetForId(this.chunkId, context.tileMap.resolution);
    const index = mapOffsetToIndex(image, mapOffset, this.level);
    const tileSize = levelRatio(image.lodData, this.level);
    const tileMapLocation = computeTileMapLocation(
      image.lodData,
      this.level,
      index,
      image.mapPosition,
    );
    if (this.tile === null) {
      context.tileMap.storeTileLocation(tileMapLocation, tileSize, TRANSPARENT_TILE);
      return;
    }
    assertDefined(this.slot, "preload didn't finish");

    context.slotMap.set(this.level, this.chunkId, this.slot);
    const tileSlotCoordinate = context.physicalTextures.tileSlotCoordinate(this.slot);
    // TODO: use best level from context.currentState?
    context.tileMap.storeTileLocation(tileMapLocation, tileSize, {
      textureIndex: context.physicalTextures.tileSlotTexture(this.slot),
      x: tileSlotCoordinate.x,
      y: tileSlotCoordinate.y,
      size: tileSize,
      ratio: this.tile.uv?.width ?? 1.0,
      atlasX: this.tile.uv?.x ?? 0.0,
      atlasY: 1.0 - (this.tile.uv?.y ?? 1.0) - (this.tile.uv?.height ?? 0.0),
    });
  }

  toString() {
    return `+${this.chunkId}@${this.level}`;
  }
}

registerType<LoadOperation>('LoadOperation', {
  replacer: (operation: LoadOperation) => {
    return {
      imageId: operation.imageId,
      level: operation.level,
      chunkId: operation.chunkId,
      reservedSlots: operation.reservedSlots,
      usedSlots: operation.usedSlots,
      affectedChunkIds: operation.affectedChunkIds,
      causeChunkIds: operation.causeChunkIds,
    };
  },
  reviver: function (value: any): LoadOperation {
    const operation = new LoadOperation(
      value.imageId,
      value.level,
      value.chunkId,
      value.causeChunkIds,
    );
    operation.reservedSlots = value.reservedSlots;
    operation.usedSlots = value.usedSlots;
    operation.affectedChunkIds = value.affectedChunkIds;
    return operation;
  },
});

export class UnloadOperation implements Operation, Serializable {
  name: string;
  imageId: string;
  reservedSlots: number;
  usedSlots: number;
  affectedChunkIds: number[];

  __className(): string {
    return 'UnloadOperation';
  }

  constructor(
    imageId: string,
    public level: number,
    public chunkId: number,
    public causeChunkIds: number[],
  ) {
    this.name = 'unload';
    this.imageId = imageId;
  }

  applyState(context: SelectionContext) {
    removeLevelFromState(context, context.images.get(this.imageId)!, this.level, this.chunkId);
  }

  revertState(context: SelectionContext) {
    addLevelToState(context, context.images.get(this.imageId)!, this.level, this.chunkId);
  }

  initialize(context: SelectionContext) {
    const image = context.images.get(this.imageId)!;
    this.reservedSlots = 0;
    this.affectedChunkIds = chunksCoveredByTile(
      image.lodData,
      this.chunkId,
      this.level,
      context.tileMapResolution,
    );
  }

  preload(context: LoaderContext): Promise<void> {
    const image = context.images.get(this.imageId)!;
    const mapOffset = context.tileMap.chunkOffset(this.chunkId);
    const index = mapOffsetToIndex(image, mapOffset, this.level);
    const lodLevel = image.lodData.curator_lods[this.level];
    const tile = lodLevel.textures[index];
    assertDefined(tile, 'Invalid tile index');

    const slot = context.slotMap.get(this.level, this.chunkId);
    const cantEmptySlot =
      (slot !== undefined && context.physicalTextures.slotUseCount(slot) > 1) ?? false;

    this.usedSlots = context.slotMap.isEmpty(this.level, this.chunkId)
      ? 0
      : -1 + (cantEmptySlot ? 1 : 0);
    return Promise.resolve();
  }

  execute(context: LoaderContext) {
    const image = context.images.get(this.imageId)!;
    const mapOffset = context.tileMap.chunkOffset(this.chunkId);
    const index = mapOffsetToIndex(image, mapOffset, this.level);
    const tileSize = levelRatio(image.lodData, this.level);
    const tileMapLocation = computeTileMapLocation(
      image.lodData,
      this.level,
      index,
      image.mapPosition,
    );
    assertDefined(image, 'Image was removed during operation');
    cleanSlot(context, image, this.level, this.chunkId); // TODO: should it be here?
    context.tileMap.storeTileLocation(tileMapLocation, tileSize, TRANSPARENT_TILE);
  }

  cancel(_context: LoaderContext) {}

  toString() {
    return `-${this.chunkId}@${this.level}`;
  }
}

registerType<UnloadOperation>('UnloadOperation', {
  replacer: (operation: UnloadOperation) => {
    return {
      imageId: operation.imageId,
      level: operation.level,
      chunkId: operation.chunkId,
      reservedSlots: operation.reservedSlots,
      usedSlots: operation.usedSlots,
      affectedChunkIds: operation.affectedChunkIds,
      causeChunkIds: operation.causeChunkIds,
    };
  },
  reviver: function (value: any): UnloadOperation {
    const operation = new UnloadOperation(
      value.imageId,
      value.level,
      value.chunkId,
      value.causeChunkIds,
    );
    operation.reservedSlots = value.reservedSlots;
    operation.usedSlots = value.usedSlots;
    operation.affectedChunkIds = value.affectedChunkIds;
    return operation;
  },
});

export class CompositionOperation implements Operation, Serializable {
  name: string;
  imageId: string;
  reservedSlots: number;
  usedSlots: number;
  affectedChunkIds: number[];
  causeChunkIds: number[];

  __className(): string {
    return 'CompositionOperation';
  }

  constructor(name: string, public operations: Operation[]) {
    this.name = name;
    this.causeChunkIds = this.operations.flatMap(it => it.causeChunkIds);
  }

  applyState(context: SelectionContext) {
    this.operations.forEach(it => it.applyState(context));
  }

  revertState(context: SelectionContext) {
    this.operations.forEach(it => it.revertState(context));
  }

  initialize(context: SelectionContext) {
    this.operations.forEach(it => it.initialize(context));
    this.reservedSlots = this.operations.reduce((sum, it) => (sum += it.reservedSlots), 0);
    this.affectedChunkIds = this.operations.reduce(
      (union, it) => union.concat(union, it.affectedChunkIds),
      new Array<number>(),
    );
  }

  async preload(context: LoaderContext): Promise<void> {
    await Promise.all(this.operations.map(it => it.preload(context)));
    this.usedSlots = this.operations.reduce((sum, it) => (sum += it.usedSlots), 0);
  }

  execute(context: LoaderContext) {
    this.operations.forEach(it => it.execute(context));
  }

  cancel(context: LoaderContext) {
    this.operations.forEach(it => it.cancel(context));
  }

  toString() {
    return `"${this.name}" {${this.operations.map(it => it.toString()).join(', ')}}`;
  }
}

registerType<CompositionOperation>('CompositionOperation', {
  replacer: (operation: CompositionOperation) => {
    return {
      name: operation.name,
      reservedSlots: operation.reservedSlots,
      usedSlots: operation.usedSlots,
      affectedChunkIds: operation.affectedChunkIds,
      operations: operation.operations,
    };
  },
  reviver: function (value: any): CompositionOperation {
    const operation = new CompositionOperation(value.name, value.operations);
    operation.reservedSlots = value.reservedSlots;
    operation.usedSlots = value.usedSlots;
    operation.affectedChunkIds = value.affectedChunkIds;
    return operation;
  },
});
